import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain, Loader2, CheckCircle2, XCircle, Send, PartyPopper, Inbox, ExternalLink, Mic, Square, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { quizNext, quizAnswer, quizDontKnow } from "@/lib/api";

export default function QuizPage({ dark, toggleTheme }) {
  const [mode, setMode] = useState("all");
  const [card, setCard] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const recognitionRef = useRef(null);
  const baseTextRef = useRef("");
  const [listening, setListening] = useState(false);
  const [micSupported, setMicSupported] = useState(true);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setMicSupported(!!SR);
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const stopMic = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    setListening(false);
  }, []);

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Spracherkennung wird auf diesem Gerät nicht unterstützt.");
      return;
    }
    if (listening) {
      stopMic();
      return;
    }
    const rec = new SR();
    rec.lang = "de-DE";
    rec.interimResults = true;
    rec.continuous = true;
    baseTextRef.current = text ? text.trim() + " " : "";
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setText((baseTextRef.current + transcript).replace(/\s+/g, " ").trimStart());
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Mikrofon-Zugriff wurde verweigert.");
      } else if (e.error !== "aborted" && e.error !== "no-speech") {
        toast.error("Spracherkennung fehlgeschlagen.");
      }
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
      toast.info("Sprich jetzt… (Deutsch)");
    } catch {
      setListening(false);
    }
  };

  const loadNext = useCallback(async (m) => {
    stopMic();
    setLoading(true);
    setResult(null);
    setText("");
    setCard(null);
    try {
      const data = await quizNext(m);
      setCard(data.card);
      setRemaining(data.remaining);
    } catch {
      toast.error("Karte konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNext(mode);
  }, [mode, loadNext]);

  const submit = async () => {
    stopMic();
    if (!text.trim()) {
      toast.error("Bitte eine Antwort eingeben.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await quizAnswer(card.id, text);
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bewertung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const dontKnow = async () => {
    stopMic();
    setSubmitting(true);
    try {
      const res = await quizDontKnow(card.id);
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header dark={dark} toggleTheme={toggleTheme} />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Brain className="h-7 w-7 text-blue-600 dark:text-blue-400" /> Quiz
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {remaining} Karten im Pool · KI bewertet deine Antwort
            </p>
          </div>
          <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 p-1 bg-white dark:bg-slate-900">
            <button
              data-testid="quiz-mode-all"
              onClick={() => setMode("all")}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${mode === "all" ? "bg-[#0F2942] dark:bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300"}`}
            >
              Alle
            </button>
            <button
              data-testid="quiz-mode-wrong"
              onClick={() => setMode("wrong")}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${mode === "wrong" ? "bg-red-600 text-white" : "text-slate-600 dark:text-slate-300"}`}
            >
              Nur Falsche
            </button>
            <button
              data-testid="quiz-mode-unknown"
              onClick={() => setMode("unknown")}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${mode === "unknown" ? "bg-amber-500 text-white" : "text-slate-600 dark:text-slate-300"}`}
            >
              Nur Ungewusste
            </button>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : !card ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700">
              {mode === "wrong" ? (
                <>
                  <PartyPopper className="h-10 w-10 mx-auto text-emerald-500" />
                  <p className="mt-4 text-slate-700 dark:text-slate-200 font-medium">Keine falsch beantworteten Karten!</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Wechsle zu „Alle" oder lade neue Karten hoch.</p>
                </>
              ) : mode === "unknown" ? (
                <>
                  <PartyPopper className="h-10 w-10 mx-auto text-amber-500" />
                  <p className="mt-4 text-slate-700 dark:text-slate-200 font-medium">Keine „nicht gewussten" Karten!</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Markiere im Quiz Karten mit „Ich weiß es nicht", um sie hier zu üben.</p>
                </>
              ) : (
                <>
                  <Inbox className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600" />
                  <p className="mt-4 text-slate-700 dark:text-slate-200 font-medium">Noch keine Karten vorhanden.</p>
                  <Link to="/" className="mt-4 inline-block rounded-xl bg-[#0F2942] dark:bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white">
                    Dokument hochladen
                  </Link>
                </>
              )}
            </div>
          ) : (
            <div data-testid="quiz-card" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="font-mono">Karte {card.number}</span>
                <span>·</span>
                <span className="truncate">{card.deck_filename}</span>
              </div>
              <h2 className="mt-2 font-display text-xl font-bold text-slate-900 dark:text-slate-100">{card.title}</h2>
              <div className="mt-3 text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Frage</div>
              <p data-testid="quiz-question" className="mt-1 text-lg text-slate-800 dark:text-slate-100 leading-relaxed">{card.question}</p>

              <div className="mt-5 relative">
                <textarea
                  data-testid="quiz-answer-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={!!result || submitting}
                  rows={4}
                  placeholder="Schreibe oder sprich deine Antwort…"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-4 pr-14 text-sm text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                />
                {micSupported && !result && (
                  <button
                    type="button"
                    data-testid="quiz-mic-button"
                    onClick={toggleMic}
                    disabled={submitting}
                    aria-label={listening ? "Aufnahme stoppen" : "Antwort diktieren"}
                    title={listening ? "Aufnahme stoppen" : "Antwort diktieren (Gerät-Spracherkennung)"}
                    className={`absolute bottom-3 right-3 h-9 w-9 grid place-items-center rounded-full transition-colors ${
                      listening
                        ? "bg-red-500 text-white animate-pulse"
                        : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {listening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                )}
              </div>
              {listening && (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Aufnahme läuft – sprich deine Antwort…
                </p>
              )}

              {!result ? (
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    data-testid="quiz-submit-button"
                    onClick={submit}
                    disabled={submitting}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F2942] dark:bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-[#1E3A5F] dark:hover:bg-blue-500 disabled:opacity-60"
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird bewertet…</> : <><Send className="h-4 w-4" /> Antwort prüfen</>}
                  </button>
                  <button
                    data-testid="quiz-dont-know-button"
                    onClick={dontKnow}
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/50 disabled:opacity-60"
                  >
                    <HelpCircle className="h-4 w-4" /> Ich weiß es nicht
                  </button>
                </div>
              ) : (
                <div className="mt-5">
                  {(() => {
                    const oc = result.outcome || (result.correct ? "correct" : "wrong");
                    const cfg = {
                      correct: { box: "border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30", title: "text-emerald-700 dark:text-emerald-300", label: "Richtig beantwortet", Icon: CheckCircle2, ic: "text-emerald-500" },
                      wrong: { box: "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30", title: "text-red-700 dark:text-red-300", label: "Leider falsch", Icon: XCircle, ic: "text-red-500" },
                      unknown: { box: "border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30", title: "text-amber-700 dark:text-amber-300", label: "Nicht gewusst", Icon: HelpCircle, ic: "text-amber-500" },
                    }[oc];
                    const Icon = cfg.Icon;
                    return (
                      <div
                        data-testid="quiz-result"
                        data-correct={result.correct}
                        data-outcome={oc}
                        className={`flex items-start gap-3 rounded-xl border p-4 ${cfg.box}`}
                      >
                        <Icon className={`h-6 w-6 shrink-0 ${cfg.ic}`} />
                        <div>
                          <div className={`font-bold ${cfg.title}`}>{cfg.label}</div>
                          <p className="mt-0.5 text-sm text-slate-700 dark:text-slate-300">{result.feedback}</p>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Musterantwort</div>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{result.correct_answer}</p>
                    {result.sources?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {result.sources.map((s) => (
                          <a
                            key={s.number}
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-950/50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100"
                          >
                            ({s.number}) {s.aktenzeichen || s.court || "Quelle"} <ExternalLink className="h-3 w-3" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    data-testid="quiz-next-button"
                    onClick={() => loadNext(mode)}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F2942] dark:bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-[#1E3A5F] dark:hover:bg-blue-500"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Gelesen – nächste Karte
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
